import React, { useState, useEffect } from 'react';
import useAuth from '../../hooks/useAuth';
import './twofactormodal.css';

const TwoFactorModal = ({ onClose, onSuccess }) => {
  const { generate2FASecret, enable2FA, isLoading } = useAuth();
  
  const [step, setStep] = useState(1); // 1=QR+Verify, 2=BackupCodes
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [error, setError] = useState('');
  const [generating, setGenerating] = useState(true);

  // ✅ Auto-generate QR code when modal opens
  useEffect(() => {
    const generateQR = async () => {
      setGenerating(true);
      const result = await generate2FASecret();
      
      if (result.success) {
        setQrCode(result.qrCode);
        setSecret(result.secret);
      } else {
        setError(result.error || 'Failed to generate QR code');
      }
      setGenerating(false);
    };

    generateQR();
  }, [generate2FASecret]);

  // Verify and Enable
  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');

    if (verificationCode.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    const result = await enable2FA(secret, verificationCode);

    if (result.success) {
      setBackupCodes(result.backupCodes || []);
      setStep(2);
    } else {
      setError(result.error || 'Invalid code');
      setVerificationCode('');
    }
  };

  const handleFinish = () => {
    if (onSuccess) onSuccess();
    onClose();
  };

  const downloadBackupCodes = () => {
    const text = backupCodes.join('\n');
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        
        {/* Step 1: Scan QR & Verify (Merged) */}
        {step === 1 && (
          <>
<div>
            <h2>🔐 Enable Two-Factor Authentication</h2>
            <p className="modal-description">
              Scan this QR code with your authenticator app
            </p>
            
            {generating ? (
              <div className="qr-container">
                <div className="spinner-large"></div>
                <p className="generating-text">Generating QR code...</p>
              </div>
            ) : qrCode ? (
              <div className="qr-container">
                <img src={qrCode} alt="QR Code" className="qr-image" />
                <p className="qr-hint">
                  Use Google Authenticator, Authy, or Microsoft Authenticator
                </p>
              </div>
            ) : (
              <div className="error-text">⚠️ {error}</div>
            )}
</div>
            <form onSubmit={handleVerify}>
              <label>Enter the 6-digit code from your app:</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="code-input"
                autoFocus
                disabled={generating}
              />

              {error && !generating && <div className="error-text">⚠️ {error}</div>}

              <div className="modal-actions">
                <button 
                  type="submit" 
                  className="modal-btn primary" 
                  disabled={isLoading || verificationCode.length !== 6 || generating}
                >
                  {isLoading ? 'Verifying...' : 'Verify & Enable'}
                </button>
                <button 
                  type="button" 
                  onClick={onClose} 
                  className="modal-btn secondary"
                  disabled={isLoading}
                >
                  Cancel
                </button>
              </div>
            </form>
          </>
        )}

        {/* Step 2: Backup Codes */}
        {step === 2 && (
          <div>
            <h2>✅ 2FA Enabled!</h2>
            <p className="modal-description">
              Save these backup codes in a safe place. Each can only be used once.
            </p>
            
            <div className="backup-codes-grid">
              {backupCodes.map((code, idx) => (
                <div key={idx} className="backup-code-item">{code}</div>
              ))}
            </div>

            <div className="modal-actions">
              <button onClick={downloadBackupCodes} className="modal-btn secondary">
                📥 Download Codes
              </button>
              <button onClick={handleFinish} className="modal-btn primary">
                ✅ Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TwoFactorModal;